import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ProgressCard } from "../common/ProgressCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Cloud, Plus, Pencil, Trash2, Loader2, Zap, Users, FileText, Paperclip, ChevronDown, Info, BarChart2 } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { DEFAULT_FUEL_ENTRY, DEFAULT_ELECTRICITY_ENTRY, DEFAULT_REFRIGERANT_ENTRY } from "@/contexts/wizard/useEnvironmentState";
import { calculateRefrigerantEmissions as calculateRefrigerantEmissionsApi } from "@/service/emissions";
import { REFRIGERANT_TYPES } from "@shared/factors/refrigerant-types";
import { HOMEWORKING_FACTORS, WATER_FACTORS } from "@shared/factors/emission-factors";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { UploadSectionIcon } from "../common/UploadSectionIcon";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import BusinessTravelFormSection from "./BusinessTravelFormSection";
import EmployeeCommutingFormSection from "./EmployeeCommutingFormSection";
import FreightTransportFormSection from "./FreightTransportFormSection";
import CompanyExpensesFormSection from "./CompanyExpensesFormSection";
import AccommodationFormSection from "./AccommodationFormSection";
import { PeriodSelector } from "@/components/workflow/common/PeriodSelector";
import { hasCoverage } from "@shared/period-coverage";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";

function InfoGlyph() {
  return <Info className="h-[15px] w-[15px] text-slate-500" strokeWidth={1.8} />;
}

function scaleGHGIntensity(value: number, currency: string): { displayValue: string; unitCurrency: string } {
  if (value<0.0001) {
    return { displayValue: (value).toFixed(4), unitCurrency: `${currency}` };
  }
  return { displayValue: (value).toFixed(4), unitCurrency: `${currency}` };
}

function SectionTitle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="text-xl font-bold leading-7 text-slate-900">{title}</h4>
        <InfoGlyph />
      </div>
      <p className="text-sm leading-5 text-primary">{description}</p>
    </div>
  );
}

function EntryDot({ className }: { className: string }) {
  return <span className={cn("h-2 w-2 rounded-full", className)} />;
}

function EntrySeparator() {
  return <span className="h-1 w-1 rounded-full bg-slate-300" aria-hidden="true" />;
}

function SummaryMetricCard({
  icon,
  title,
  description,
  value,
  unit,
  shellClassName,
  valueTestId,
  footer,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  value: string;
  unit: string;
  shellClassName: string;
  valueTestId?: string;
  footer?: ReactNode;
}) {
  return (
    <section className={cn("flex flex-col gap-6 rounded-[28px] p-8 shadow-sm", shellClassName)}>
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white">
            {icon}
          </div>
          <div className="flex flex-col gap-1">
            <h4 className="text-xl font-bold leading-7 text-primary">{title}</h4>
            <p className="text-sm leading-5 text-gray-500">{description}</p>
          </div>
        </div>
        <div className="text-left md:text-right">
          <div className="text-3xl font-bold leading-9 text-primary" data-testid={valueTestId}>
            {value}
          </div>
          <div className="text-sm font-bold leading-5 text-gray-400">{unit}</div>
        </div>
      </div>
      {footer}
    </section>
  );
}

function Scope3Row({
  title,
  description,
  value,
  count,
  open,
  onToggle,
  disabled,
  children,
  testId,
}: {
  title: string;
  description: string;
  value: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  disabled: boolean;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <Collapsible open={open} onOpenChange={onToggle}>
      <div className="wizard-card flex flex-col gap-4 border border-slate-200">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full flex-col gap-4 px-6 py-5 text-left transition-colors hover:bg-slate-50 md:flex-row md:items-center md:justify-between"
           
          >
            <div className="flex items-start gap-4">
              <ChevronDown
                className={cn("mt-1 h-4 w-4 shrink-0 text-primary transition-transform", open && "rotate-180")}
              />
              <div className="flex flex-col gap-1">
                <div className="text-base font-bold leading-6 text-primary">{title}</div>
                <div className="text-xs leading-4 text-gray-500">{description}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 self-end md:self-auto">
              {count > 0 && (
                <span className="rounded-md bg-primary/10 px-2 py-1 text-[10px] font-bold leading-4 text-primary">
                  {count} entries
                </span>
              )}
              <span className="text-base font-bold leading-6 text-gray-900" data-testid={testId}>
                {value}
              </span>
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-slate-200 px-6 pb-6 pt-2">{children}</div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export function CarbonEmissionsCard({ onClose }: { onClose: () => void }) {
  const {
    generalInfo,
    assessmentId,
    assessment,
    fuelEntries,
    electricityEntries,
    refrigerantEntries,
    currentFuelEntry,
    setCurrentFuelEntry,
    currentElectricityEntry,
    setCurrentElectricityEntry,
    currentRefrigerantEntry,
    setCurrentRefrigerantEntry,
    addFuelEntry,
    addElectricityEntry,
    addRefrigerantEntry,
    deleteFuelEntry,
    deleteElectricityEntry,
    deleteRefrigerantEntry,
    updateFuelEntry,
    updateElectricityEntry,
    updateRefrigerantEntry,
    calculateFuelEmissions,
    calculateElectricityEmissions,
    carbonCompletion,
    saveStatus,
    forceSave,
    carbonSupportingDocuments,
    setCarbonSupportingDocuments,
    isLocked,
    isReadOnly,
    scope3Data,
    wasteData,
    waterData,
  } = useWizard();
  const disabled = isLocked || isReadOnly;

  const periodStart = assessment?.periodStart ? new Date(assessment.periodStart) : null;
  const periodEnd   = assessment?.periodEnd   ? new Date(assessment.periodEnd)   : null;

  const showFuelCoverageWarning = periodStart && periodEnd
    ? fuelEntries.length > 0 && !hasCoverage(fuelEntries, periodStart, periodEnd)
    : false;

  const showElecCoverageWarning = periodStart && periodEnd
    ? electricityEntries.length > 0 && !hasCoverage(electricityEntries, periodStart, periodEnd)
    : false;
  const { toast } = useToast();

  const [financialInfo, setFinancialInfo] = useState<{ netTurnover: string | null; reportingCurrency: string | null }>({
    netTurnover: null,
    reportingCurrency: null,
  });

  useEffect(() => {
    if (!assessmentId) return;
    fetch(`/api/assessments/${assessmentId}/general/financial-info`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setFinancialInfo(data))
      .catch(() => {/* silently fall back to generalInfo */});
  }, [assessmentId]);

  // Use fetched financial info if available, fall back to WizardContext (already-loaded General tab)
  const netTurnover = financialInfo.netTurnover ?? generalInfo.netTurnover;
  const reportingCurrency = financialInfo.reportingCurrency ?? generalInfo.reportingCurrency;

  const [expandedScope3Sections, setExpandedScope3Sections] = useState<Set<string>>(new Set());
  const [editingFuelEntryId, setEditingFuelEntryId] = useState<string | null>(null);
  const [editingElectricityEntryId, setEditingElectricityEntryId] = useState<string | null>(null);
  const [editingRefrigerantEntryId, setEditingRefrigerantEntryId] = useState<string | null>(null);

  useEffect(() => {
    const sectionsWithData = new Set<string>();
    if (scope3Data.businessTravelEntries.length > 0) sectionsWithData.add("businessTravel");
    if (scope3Data.employeeCommutingEntries.length > 0) sectionsWithData.add("employeeCommuting");
    if (scope3Data.freightEntries.length > 0) sectionsWithData.add("freight");
    if (scope3Data.spendBasedEntries.length > 0) sectionsWithData.add("companyExpenses");
    if (scope3Data.accommodationEntries.length > 0) sectionsWithData.add("accommodation");
    setExpandedScope3Sections(sectionsWithData);
  }, [
    scope3Data.businessTravelEntries.length,
    scope3Data.employeeCommutingEntries.length,
    scope3Data.freightEntries.length,
    scope3Data.spendBasedEntries.length,
    scope3Data.accommodationEntries.length,
  ]);

  const toggleScope3Section = (sectionId: string) => {
    setExpandedScope3Sections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const pendingSaveRef = useRef(false);
  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      void forceSave();
    }
  }, [
    fuelEntries,
    electricityEntries,
    refrigerantEntries,
    scope3Data.businessTravelEntries,
    scope3Data.employeeCommutingEntries,
    scope3Data.freightEntries,
    scope3Data.accommodationEntries,
    wasteData.scope3WasteData?.scope3WasteTco2e,
    waterData.totalWithdrawal,
    waterData.annualConsumption,
    forceSave,
  ]);

  const scope1Refrigerant = useMemo(() => refrigerantEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0), [refrigerantEntries]);
  const scope1Total = useMemo(() => fuelEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0) + scope1Refrigerant, [fuelEntries, scope1Refrigerant]);
  const scope1Stationary = useMemo(
    () => fuelEntries.filter((entry) => entry.activityType === "stationary").reduce((sum, entry) => sum + (entry.tco2e || 0), 0),
    [fuelEntries]
  );
  const scope1Mobile = useMemo(
    () => fuelEntries.filter((entry) => entry.activityType === "vehicles").reduce((sum, entry) => sum + (entry.tco2e || 0), 0),
    [fuelEntries]
  );
  const scope2Total = useMemo(() => electricityEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0), [electricityEntries]);

  const scope3Homeworking = useMemo(() => {
    const fte = parseFloat(generalInfo.totalEmployees) || 0;
    const homeworkingPct = parseFloat(generalInfo.homeworkingPercent) || 0;
    if (fte <= 0 || homeworkingPct <= 0) return 0;
    return (
      fte *
      (homeworkingPct / 100) *
      HOMEWORKING_FACTORS.hours_per_day *
      HOMEWORKING_FACTORS.working_days_per_year *
      HOMEWORKING_FACTORS.kgco2e_per_hour
    ) / 1000;
  }, [generalInfo.totalEmployees, generalInfo.homeworkingPercent]);

  const scope3BusinessTravel = useMemo(
    () => scope3Data.businessTravelEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0),
    [scope3Data.businessTravelEntries]
  );
  const scope3EmployeeCommuting = useMemo(
    () => scope3Data.employeeCommutingEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0),
    [scope3Data.employeeCommutingEntries]
  );
  const scope3Freight = useMemo(
    () => scope3Data.freightEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0),
    [scope3Data.freightEntries]
  );
  const scope3CompanyExpenses = useMemo(
    () => scope3Data.spendBasedEntries.reduce((sum, entry) => sum + (entry.tco2e || 0), 0),
    [scope3Data.spendBasedEntries]
  );
  const scope3Accommodation = useMemo(
    () => scope3Data.accommodationEntries.reduce((sum, entry) => sum + (entry.tco2e ?? 0), 0),
    [scope3Data.accommodationEntries]
  );
  const scope3Waste = useMemo(() => wasteData.scope3WasteData?.scope3WasteTco2e || 0, [wasteData.scope3WasteData?.scope3WasteTco2e]);
  const scope3Water = useMemo(() => {
    const withdrawal = waterData.totalWithdrawal || 0;
    const consumption = waterData.annualConsumption || 0;
    return (
      withdrawal * WATER_FACTORS.supply_kgco2e_per_cubic_meter +
      consumption * WATER_FACTORS.treatment_kgco2e_per_cubic_meter
    ) / 1000;
  }, [waterData.totalWithdrawal, waterData.annualConsumption]);

  const scope3Total = useMemo(
    () => scope3Homeworking + scope3BusinessTravel + scope3EmployeeCommuting + scope3Freight + scope3CompanyExpenses + scope3Accommodation + scope3Waste + scope3Water,
    [scope3Homeworking, scope3BusinessTravel, scope3EmployeeCommuting, scope3Freight, scope3CompanyExpenses, scope3Accommodation, scope3Waste, scope3Water]
  );
  const totalEmissions = useMemo(() => scope1Total + scope2Total + scope3Total, [scope1Total, scope2Total, scope3Total]);

  const ghgIntensity = useMemo(() => {
    const turnover = parseFloat(netTurnover ?? "") || 0;
    if (turnover <= 0 || totalEmissions <= 0) return null;
    return totalEmissions / turnover;
  }, [totalEmissions, netTurnover]);

  const handleAddFuelEntry = async () => {
    const result = await addFuelEntry();
    if (!result.ok) {
      toast({ title: result.error || "Failed to add fuel entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Fuel entry added",
      description: result.tco2e
        ? `${result.tco2e.toFixed(3)} tCO2e`
        : "Calculating...",
    });
  };

  const handleAddElectricityEntry = async () => {
    const result = await addElectricityEntry();
    if (!result.ok) {
      toast({ title: result.error || "Failed to add electricity entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Electricity entry added",
      description: result.tco2e
        ? `${result.tco2e.toFixed(3)} tCO2e`
        : "Calculating...",
    });
  };

  const handleAddRefrigerantEntry = async () => {
    const result = await addRefrigerantEntry();
    if (!result.ok) {
      toast({ title: result.error || "Failed to add refrigerant entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({ title: "Refrigerant entry added" });
  };

  const handleDeleteFuelEntry = (id: string) => {
    deleteFuelEntry(id);
    toast({ title: "Entry deleted" });
    pendingSaveRef.current = true;
  };

  const handleDeleteElectricityEntry = (id: string) => {
    deleteElectricityEntry(id);
    toast({ title: "Entry deleted" });
    pendingSaveRef.current = true;
  };

  const handleDeleteRefrigerantEntry = (id: string) => {
    deleteRefrigerantEntry(id);
    toast({ title: "Entry deleted" });
    pendingSaveRef.current = true;
  };

  // --- Fuel edit handlers ---
  const handleEditFuelEntry = (entry: { id: string; activityType?: any; fuelType?: any; period?: any; quantity?: any; unit?: any; allocationStationaryPct?: any; allocationMobilePct?: any; evidenceId?: any; evidenceName?: any }) => {
    setCurrentFuelEntry({
      activityType: entry.activityType,
      fuelType: entry.fuelType,
      period: entry.period,
      quantity: entry.quantity,
      unit: entry.unit,
      allocationStationaryPct: entry.allocationStationaryPct,
      allocationMobilePct: entry.allocationMobilePct,
      evidenceId: entry.evidenceId,
      evidenceName: entry.evidenceName,
    });
    setEditingFuelEntryId(entry.id);
  };

  const handleCancelFuelEdit = () => {
    setCurrentFuelEntry(DEFAULT_FUEL_ENTRY);
    setEditingFuelEntryId(null);
  };

  const handleUpdateFuelEntry = async () => {
    const id = editingFuelEntryId!;
    updateFuelEntry(id, { ...currentFuelEntry, calculating: true });
    try {
      const result = await calculateFuelEmissions(currentFuelEntry);
      updateFuelEntry(id, { tco2e: result.tco2e, energyMwh: result.energyMwh ?? undefined, calculating: false });
      pendingSaveRef.current = true;
      setCurrentFuelEntry(DEFAULT_FUEL_ENTRY);
      setEditingFuelEntryId(null);
      toast({
        title: "Entry updated",
        description: result.tco2e != null ? `${result.tco2e.toFixed(3)} tCO₂e` : undefined,
      });
    } catch {
      toast({ title: "Failed to update entry", variant: "destructive" });
      updateFuelEntry(id, { calculating: false });
    }
  };

  // --- Electricity edit handlers ---
  const handleEditElectricityEntry = (entry: { id: string; period?: any; consumption?: any; unit?: any; meterId?: any; gridPercent?: any; selfGeneratedPercent?: any; isRenewable?: any; renewableSource?: any; evidenceId?: any; evidenceName?: any }) => {
    setCurrentElectricityEntry({
      period: entry.period,
      consumption: entry.consumption,
      unit: entry.unit,
      meterId: entry.meterId,
      gridPercent: entry.gridPercent,
      selfGeneratedPercent: entry.selfGeneratedPercent,
      isRenewable: entry.isRenewable,
      renewableSource: entry.renewableSource,
      evidenceId: entry.evidenceId,
      evidenceName: entry.evidenceName,
    });
    setEditingElectricityEntryId(entry.id);
  };

  const handleCancelElectricityEdit = () => {
    setCurrentElectricityEntry(DEFAULT_ELECTRICITY_ENTRY);
    setEditingElectricityEntryId(null);
  };

  const handleUpdateElectricityEntry = async () => {
    const id = editingElectricityEntryId!;
    const { consumption = 0, unit = "kwh", gridPercent = 100, selfGeneratedPercent = 0 } = currentElectricityEntry;
    const consumptionKwh = unit === "mwh" ? consumption * 1000 : consumption;
    const gridKwh = consumptionKwh * (gridPercent / 100);
    const selfGeneratedKwh = consumptionKwh * (selfGeneratedPercent / 100);
    updateElectricityEntry(id, { ...currentElectricityEntry, gridKwh, selfGeneratedKwh, calculating: true });
    try {
      const tco2e = await calculateElectricityEmissions({ ...currentElectricityEntry, gridKwh, selfGeneratedKwh });
      updateElectricityEntry(id, { tco2e, calculating: false });
      pendingSaveRef.current = true;
      setCurrentElectricityEntry(DEFAULT_ELECTRICITY_ENTRY);
      setEditingElectricityEntryId(null);
      toast({
        title: "Entry updated",
        description: tco2e != null ? `${tco2e.toFixed(3)} tCO₂e` : undefined,
      });
    } catch {
      toast({ title: "Failed to update entry", variant: "destructive" });
      updateElectricityEntry(id, { calculating: false });
    }
  };

  // --- Refrigerant edit handlers ---
  const handleEditRefrigerantEntry = (entry: { id: string; refrigerantType?: any; leakedAmount?: any; unit?: any; period?: any; evidenceId?: any; evidenceName?: any }) => {
    setCurrentRefrigerantEntry({
      refrigerantType: entry.refrigerantType,
      leakedAmount: entry.leakedAmount,
      unit: entry.unit,
      period: entry.period,
      evidenceId: entry.evidenceId,
      evidenceName: entry.evidenceName,
    });
    setEditingRefrigerantEntryId(entry.id);
  };

  const handleCancelRefrigerantEdit = () => {
    setCurrentRefrigerantEntry(DEFAULT_REFRIGERANT_ENTRY);
    setEditingRefrigerantEntryId(null);
  };

  const handleUpdateRefrigerantEntry = async () => {
    const id = editingRefrigerantEntryId!;
    updateRefrigerantEntry(id, { ...currentRefrigerantEntry, calculating: true });
    try {
      const tco2e = await calculateRefrigerantEmissionsApi(currentRefrigerantEntry);
      updateRefrigerantEntry(id, { tco2e, calculating: false });
      pendingSaveRef.current = true;
      setCurrentRefrigerantEntry(DEFAULT_REFRIGERANT_ENTRY);
      setEditingRefrigerantEntryId(null);
      toast({ title: "Entry updated" });
    } catch {
      toast({ title: "Failed to update entry", variant: "destructive" });
      updateRefrigerantEntry(id, { calculating: false });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Carbon Emissions (B3)"}
        description={"Record the greenhouse gases your business produces directly (Scope 1) and indirectly through energy use (Scope 2). If new, start with fuel and electricity bills"}
        onClose={onClose}
        onSave={() => {
          void forceSave().catch((err) => {
            console.error("Save failed:", err);
            toast({
              title: "Save failed",
              description: "Could not save changes. Please try again.",
              variant: "destructive",
            });
          });
        }}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"Environment"}
      />

      {disabled && <LockedBanner />}

      <SectionCard header={<SectionTitle
          title={"Fuel (Scope 1) - Diesel, Petrol, LPG Only"}
          description={"For each fuel purchase or billing period, enter the fuel type, how much you used, and the unit of measurement. Use receipts or fuel logs as reference"}
        />}>

        <div className="form-surface-card-lg">
          <div className="flex flex-col gap-5 px-5 pb-6 pt-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label className="form-label">{"Activity Type *"}</Label>
                <Select
                  value={currentFuelEntry.activityType || "stationary"}
                  onValueChange={(value) => setCurrentFuelEntry((prev) => ({
                    ...prev,
                    activityType: value as any,
                    fuelType: value !== "stationary" && prev.fuelType === "wood" ? "diesel" : prev.fuelType,
                    unit: value !== "stationary" && prev.fuelType === "wood" ? "liters" : prev.unit,
                  }))}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-fuel-activity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stationary">{"Stationary Generator"}</SelectItem>
                    <SelectItem value="vehicles">{"Mobile"}</SelectItem>
                    <SelectItem value="mixed">{"Mixed-Use"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="form-label">{"Fuel Type *"}</Label>
                <Select
                  value={currentFuelEntry.fuelType || "diesel"}
                  onValueChange={(value) => setCurrentFuelEntry((prev) => ({
                    ...prev,
                    fuelType: value as any,
                    unit: value === "wood" ? "kg" : (prev.fuelType === "wood" ? "liters" : prev.unit),
                  }))}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-fuel-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="petrol">{"Petrol"}</SelectItem>
                    <SelectItem value="lpg">{"LPG"}</SelectItem>
                    {currentFuelEntry.activityType === "stationary" && (
                      <SelectItem value="wood">{"Fuelwood"}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentFuelEntry.period ?? null}
              onChange={(value) => setCurrentFuelEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label className="form-label">{"Quantity *"}</Label>
                <Input
                  className="form-input"
                  type="number"
                  placeholder={"e.g. 200"}
                  value={currentFuelEntry.quantity ?? ""}
                  onChange={(e) => setCurrentFuelEntry((prev) => ({ ...prev, quantity: parseFloat(e.target.value) || 0 }))}
                  data-testid="input-fuel-quantity"
                  disabled={disabled}
                />
              </div>
              <div className="space-y-2">
                <Label className="form-label">{"Unit *"}</Label>
                <Select
                  value={currentFuelEntry.unit || "liters"}
                  onValueChange={(value) => setCurrentFuelEntry((prev) => ({ ...prev, unit: value as any }))}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-fuel-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currentFuelEntry.fuelType !== "wood" && (
                      <SelectItem value="liters">{"Litres"}</SelectItem>
                    )}
                    <SelectItem value="kg">{"kg"}</SelectItem>
                    {currentFuelEntry.fuelType !== "wood" && (
                      <>
                        <SelectItem value="us-gallon">{"US Gallon"}</SelectItem>
                        <SelectItem value="uk-gallon">{"UK Gallon"}</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {currentFuelEntry.activityType === "mixed" && (
              <div className="rounded-[24px] border border-slate-100 bg-primary/10 px-4 py-4">
                <Label className="mb-3 block text-sm font-bold leading-5 text-primary">
                  Fuel Usage Split
                </Label>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="form-label">{"Stationary (%)"}</Label>
                    <Input
                      className="form-input"
                      type="number"
                      placeholder={"100"}
                      min="0"
                      max="100"
                      value={currentFuelEntry.allocationStationaryPct ?? ""}
                      onChange={(e) =>
                        setCurrentFuelEntry((prev) => ({
                          ...prev,
                          allocationStationaryPct: parseFloat(e.target.value) || undefined,
                        }))
                      }
                      data-testid="input-fuel-allocation-stationary"
                      disabled={disabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="form-label">{"Mobile (%)"}</Label>
                    <Input
                      className="form-input"
                      type="number"
                      placeholder={"0"}
                      min="0"
                      max="100"
                      value={currentFuelEntry.allocationMobilePct ?? ""}
                      onChange={(e) =>
                        setCurrentFuelEntry((prev) => ({
                          ...prev,
                          allocationMobilePct: parseFloat(e.target.value) || undefined,
                        }))
                      }
                      data-testid="input-fuel-allocation-mobile"
                      disabled={disabled}
                    />
                  </div>
                </div>
                <p className="mt-3 text-sm leading-5 text-primary">{"For Mixed-Use: Enter % split between stationary and mobile (must total 100%)"}</p>
              </div>
            )}

            {carbonSupportingDocuments.length > 0 && (
              <div className="space-y-2">
                <Label className="form-label">{"Attach Document (Optional)"}</Label>
                <Select
                  value={currentFuelEntry.evidenceId || "none"}
                  onValueChange={(value) => {
                    if (value === "none") {
                      setCurrentFuelEntry((prev) => ({ ...prev, evidenceId: undefined, evidenceName: undefined }));
                      return;
                    }
                    const doc = carbonSupportingDocuments.find((item) => item.id === value);
                    if (doc) {
                      setCurrentFuelEntry((prev) => ({ ...prev, evidenceId: doc.id, evidenceName: doc.name }));
                    }
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-fuel-evidence">
                    <SelectValue placeholder={"Select document"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{"None"}</SelectItem>
                    {carbonSupportingDocuments.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          <span className="truncate">{doc.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs leading-4 text-primary">{"Attach fuel receipts for the reporting year"}</p>
              </div>
            )}

            {editingFuelEntryId ? (
              <div className="flex gap-2">
                <Button onClick={handleUpdateFuelEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelFuelEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="form-add-button"
                onClick={handleAddFuelEntry}
                data-testid="button-add-fuel"
                disabled={disabled}
              >
                <Plus className="mr-2 h-3" /> {"Add Fuel Entry"}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold leading-5 text-slate-700">{"Fuel Entries"}</h5>
            {fuelEntries.length > 0 && (
              <span className="rounded-md bg-primary/10 px-2 py-1 text-[10px] font-bold leading-4 text-primary">
                {`${fuelEntries.length} entries`}
              </span>
            )}
          </div>

          {fuelEntries.length > 0 && (
            <div className="flex flex-col gap-2">
              {fuelEntries.map((entry) => (
                <div
                  key={entry.id}
                  className={cn(
                    "flex flex-col gap-3 rounded-[24px] border border-slate-100 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between",
                    editingFuelEntryId === entry.id && "outline outline-2 outline-primary/30"
                  )}
                  data-testid={`fuel-entry-${entry.id}`}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <EntryDot
                      className={
                        entry.activityType === "vehicles"
                          ? "bg-orange-400"
                          : entry.activityType === "mixed"
                            ? "bg-violet-400"
                            : "bg-sky-400"
                      }
                    />
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium leading-5 text-slate-900">
                        <span className="capitalize">{entry.activityType}</span>
                        <EntrySeparator />
                        <span className="capitalize">{entry.fuelType}</span>
                        <EntrySeparator />
                        <span>
                          {entry.quantity} {entry.unit === "liters" ? "L" : entry.unit}
                        </span>
                        {entry.energyMwh && (
                          <>
                            <EntrySeparator />
                            <span>{entry.energyMwh} MWh</span>
                          </>
                        )}
                        <EntrySeparator />
                        <span className="text-primary">
                          {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                        </span>
                        {entry.activityType === "mixed" && (entry.allocationStationaryPct || entry.allocationMobilePct) && (
                          <>
                            <EntrySeparator />
                            <span className="text-primary">
                              {`Stationary: ${entry.allocationStationaryPct || 0}% / Mobile: ${entry.allocationMobilePct || 0}%`}
                            </span>
                          </>
                        )}
                      </div>
                      {entry.evidenceId && entry.evidenceName && (
                        <button
                          type="button"
                          onClick={() => window.open(`/api/documents/${entry.evidenceId}`, "_blank")}
                          className="flex items-center gap-2 text-xs font-medium leading-4 text-primary hover:text-slate-700"
                        >
                          <Paperclip className="h-3 w-3" />
                          {entry.evidenceName}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 self-end md:self-auto">
                    {entry.calculating ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {"Calculating..."}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        {entry.tco2e !== null
                          ? `${entry.tco2e.toFixed(3)} tCO2e`
                          : "N/A"}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditFuelEntry(entry)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      disabled={disabled || (editingFuelEntryId !== null && editingFuelEntryId !== entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteFuelEntry(entry.id)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      data-testid={`button-delete-fuel-${entry.id}`}
                      disabled={disabled || editingFuelEntryId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showFuelCoverageWarning && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Fuel entries don't cover the full assessment period.
            </div>
          )}

          <div className="rounded-[24px] border border-slate-100 bg-primary/10 px-6 py-4">
            <p className="text-sm leading-5 text-primary">
              <span className="font-bold">{"Mixed-Use Tip:"}</span>{" "}
              {"If fuel is shared between stationary and mobile uses, specify percentage allocation."}
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard header={<SectionTitle
          title={"Refrigerants (Scope 1, Optional)"}
          description={"If you manage AC/refrigeration, enter refrigerant type and leaked amount"}
        />}>

        <div className="form-surface-card-lg">
          <div className="flex flex-col gap-5 px-5 pb-6 pt-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label className="form-label">Refrigerant Type *</Label>
                <Select
                  value={currentRefrigerantEntry.refrigerantType || ""}
                  onValueChange={(value) => setCurrentRefrigerantEntry((prev) => ({ ...prev, refrigerantType: value }))}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-refrigerant-type">
                    <SelectValue placeholder="Select refrigerant type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REFRIGERANT_TYPES.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="form-label">{"Leaked Amount (kg) *"}</Label>
                <Input
                  className="form-input"
                  type="number"
                  placeholder={"0"}
                  value={currentRefrigerantEntry.leakedAmount ?? ""}
                  onChange={(e) => setCurrentRefrigerantEntry((prev) => ({ ...prev, leakedAmount: parseFloat(e.target.value) || 0 }))}
                  data-testid="input-refrigerant-amount"
                  disabled={disabled}
                />
              </div>
            </div>

            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentRefrigerantEntry.period ?? null}
              onChange={(value) => setCurrentRefrigerantEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />

            {carbonSupportingDocuments.length > 0 && (
              <div className="space-y-2">
                <Label className="form-label">{"Attach Document (Optional)"}</Label>
                <Select
                  value={currentRefrigerantEntry.evidenceId || "none"}
                  onValueChange={(value) => {
                    if (value === "none") {
                      setCurrentRefrigerantEntry((prev) => ({ ...prev, evidenceId: undefined, evidenceName: undefined }));
                      return;
                    }
                    const doc = carbonSupportingDocuments.find((item) => item.id === value);
                    if (doc) {
                      setCurrentRefrigerantEntry((prev) => ({ ...prev, evidenceId: doc.id, evidenceName: doc.name }));
                    }
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-refrigerant-evidence">
                    <SelectValue placeholder={"Select document"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{"None"}</SelectItem>
                    {carbonSupportingDocuments.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          <span className="truncate">{doc.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs leading-4 text-primary">{"Attach refrigerant service records for the reporting year"}</p>
              </div>
            )}

            {editingRefrigerantEntryId ? (
              <div className="flex gap-2">
                <Button onClick={handleUpdateRefrigerantEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelRefrigerantEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="form-add-button"
                onClick={handleAddRefrigerantEntry}
                data-testid="button-add-refrigerant"
                disabled={disabled}
              >
                <Plus className="mr-2 h-3 w-3" /> {"Add Refrigerant Entry"}
              </Button>
            )}
          </div>
        </div>

        {refrigerantEntries.length > 0 && (
          <div className="flex flex-col gap-2">
            <h5 className="text-sm font-bold leading-5 text-slate-700">{"Refrigerant Entries"}</h5>
            {refrigerantEntries.map((entry) => (
              <div
                key={entry.id}
                className={cn(
                  "flex flex-col gap-3 rounded-[24px] border border-slate-100 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between",
                  editingRefrigerantEntryId === entry.id && "outline outline-2 outline-primary/30"
                )}
                data-testid={`refrigerant-entry-${entry.id}`}
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <EntryDot className="bg-rose-400" />
                  <div className="min-w-0 space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium leading-5 text-slate-900">
                      <span>{REFRIGERANT_TYPES.find(r => r.id === entry.refrigerantType)?.label ?? entry.refrigerantType}</span>
                      <EntrySeparator />
                      <span>{`${entry.leakedAmount} kg leaked`}</span>
                      <EntrySeparator />
                      <span className="text-slate-500">
                        {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                      </span>
                    </div>
                    {entry.evidenceId && entry.evidenceName && (
                      <button
                        type="button"
                        onClick={() => window.open(`/api/documents/${entry.evidenceId}`, "_blank")}
                        className="flex items-center gap-2 text-xs font-medium leading-4 text-slate-500 hover:text-slate-700"
                      >
                        <Paperclip className="h-3 w-3" />
                        {entry.evidenceName}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6 self-end md:self-auto">
                  {entry.calculating ? (
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Calculating
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                      {entry.tco2e !== null ? `${entry.tco2e.toFixed(3)} tCO2e` : "N/A"}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditRefrigerantEntry(entry)}
                    className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    disabled={disabled || (editingRefrigerantEntryId !== null && editingRefrigerantEntryId !== entry.id)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRefrigerantEntry(entry.id)}
                    className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    data-testid={`button-delete-refrigerant-${entry.id}`}
                    disabled={disabled || editingRefrigerantEntryId !== null}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SummaryMetricCard
        icon={<Cloud className="h-6 w-6" />}
        title={"Scope 1 Total"}
        description={"Direct emissions from fuel combustion and refrigerants"}
        value={scope1Total.toFixed(3)}
        unit={"tCO2e"}
        shellClassName="border border-primary/10 bg-primary/10 outline outline-2 outline-offset-[-2px] outline-primary/10"
        valueTestId="text-scope1-total"
        footer={
          <div className="grid gap-4 border-t-2 border-primary/10 pt-6 text-lg font-bold leading-7 text-primary md:grid-cols-3">
            <div data-testid="text-scope1-stationary">
              {"Stationary"}: {scope1Stationary.toFixed(3)}
            </div>
            <div data-testid="text-scope1-mobile">
              {"Mobile"}: {scope1Mobile.toFixed(3)}
            </div>
            <div data-testid="text-scope1-fugitive">
              {"Fugitive"}: {scope1Refrigerant.toFixed(3)}
            </div>
          </div>
        }
      />

      <SectionCard header={<SectionTitle
          title={"Electricity (Scope 2)"}
          description={"Add each bill with billing period, consumption quantity, and unit (kWh or MWh)"}
        />}>

        <div className="form-surface-card-lg">
          <div className="flex flex-col gap-5 px-5 pb-6 pt-4">
            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentElectricityEntry.period ?? null}
              onChange={(value) => setCurrentElectricityEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <Label className="form-label">{"Consumption *"}</Label>
                <Input
                  className="form-input"
                  type="number"
                  placeholder={"e.g. 5000"}
                  value={currentElectricityEntry.consumption ?? ""}
                  onChange={(e) => setCurrentElectricityEntry((prev) => ({ ...prev, consumption: parseFloat(e.target.value) || 0 }))}
                  data-testid="input-elec-consumption"
                  disabled={disabled}
                />
              </div>
              <div className="space-y-2">
                <Label className="form-label">{"Unit *"}</Label>
                <Select
                  value={currentElectricityEntry.unit || "kwh"}
                  onValueChange={(value) => setCurrentElectricityEntry((prev) => ({ ...prev, unit: value as any }))}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-elec-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kwh">kWh</SelectItem>
                    <SelectItem value="mwh">MWh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="form-label">{"Optional: Meter/Site ID"}</Label>
              <Input
                className="form-input"
                placeholder={"From your electricity bill"}
                value={currentElectricityEntry.meterId || ""}
                onChange={(e) => setCurrentElectricityEntry((prev) => ({ ...prev, meterId: e.target.value }))}
                data-testid="input-elec-meter"
                disabled={disabled}
              />
            </div>

            <div className="border-t border-slate-200 pt-5">
              <div className="mb-4 text-sm font-bold leading-5 text-slate-700">
                {"Electricity Source Split"}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="form-label">{"Grid Electricity (%)"}</Label>
                  <Input
                    className="form-input"
                    type="number"
                    min="0"
                    max="100"
                    placeholder={"100"}
                    value={currentElectricityEntry.gridPercent ?? 100}
                    onChange={(e) => {
                      const gridPct = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                      setCurrentElectricityEntry((prev) => ({
                        ...prev,
                        gridPercent: gridPct,
                        selfGeneratedPercent: 100 - gridPct,
                      }));
                    }}
                    data-testid="input-elec-grid-percent"
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="form-label">{"Self-Generated (%)"}</Label>
                  <Input
                    className="form-input"
                    type="number"
                    min="0"
                    max="100"
                    placeholder={"0"}
                    value={currentElectricityEntry.selfGeneratedPercent ?? 0}
                    onChange={(e) => {
                      const selfPct = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                      setCurrentElectricityEntry((prev) => ({
                        ...prev,
                        selfGeneratedPercent: selfPct,
                        gridPercent: 100 - selfPct,
                      }));
                    }}
                    data-testid="input-elec-self-generated-percent"
                    disabled={disabled}
                  />
                </div>
              </div>
              <p className="mt-2 text-xs leading-4 text-slate-500">{"Percentages must sum to 100%"}</p>
            </div>

            {(currentElectricityEntry.selfGeneratedPercent ?? 0) > 0 && (
              <div className="rounded-[24px] border border-slate-100 bg-slate-50 px-4 py-4">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="is-renewable"
                    checked={currentElectricityEntry.isRenewable ?? false}
                    onCheckedChange={(checked) =>
                      setCurrentElectricityEntry((prev) => ({ ...prev, isRenewable: Boolean(checked) }))
                    }
                    disabled={disabled}
                    className="rounded border-slate-300 data-[state=checked]:border-slate-500 data-[state=checked]:bg-slate-500"
                  />
                  <Label htmlFor="is-renewable" className="cursor-pointer text-sm font-medium leading-5 text-slate-700">
                    {"Renewable share included"}
                  </Label>
                </div>

                {currentElectricityEntry.isRenewable && (
                  <div className="mt-4 space-y-2 pl-7">
                    <Label className="form-label">{"Renewable Source"}</Label>
                    <Select
                      value={currentElectricityEntry.renewableSource || "solar"}
                      onValueChange={(value) => setCurrentElectricityEntry((prev) => ({ ...prev, renewableSource: value as any }))}
                      disabled={disabled}
                    >
                      <SelectTrigger className="form-select" data-testid="select-renewable-source">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solar">{"Solar"}</SelectItem>
                        <SelectItem value="wind">{"Wind"}</SelectItem>
                        <SelectItem value="hydro">{"Hydro"}</SelectItem>
                        <SelectItem value="other">{"Other"}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs leading-4 text-slate-500">{"Optional detail for self-generated electricity"}</p>
                  </div>
                )}
              </div>
            )}

            {carbonSupportingDocuments.length > 0 && (
              <div className="space-y-2">
                <Label className="form-label">{"Attach Document (Optional)"}</Label>
                <Select
                  value={currentElectricityEntry.evidenceId || "none"}
                  onValueChange={(value) => {
                    if (value === "none") {
                      setCurrentElectricityEntry((prev) => ({ ...prev, evidenceId: undefined, evidenceName: undefined }));
                      return;
                    }
                    const doc = carbonSupportingDocuments.find((item) => item.id === value);
                    if (doc) {
                      setCurrentElectricityEntry((prev) => ({ ...prev, evidenceId: doc.id, evidenceName: doc.name }));
                    }
                  }}
                  disabled={disabled}
                >
                  <SelectTrigger className="form-select" data-testid="select-elec-evidence">
                    <SelectValue placeholder={"Select document"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{"None"}</SelectItem>
                    {carbonSupportingDocuments.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          <span className="truncate">{doc.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs leading-4 text-slate-500">{"Attach electricity bills for the reporting year"}</p>
              </div>
            )}

            {editingElectricityEntryId ? (
              <div className="flex gap-2">
                <Button onClick={handleUpdateElectricityEntry} disabled={disabled} className="form-add-button">
                  Update entry
                </Button>
                <Button onClick={handleCancelElectricityEdit} disabled={disabled} variant="ghost">
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="form-add-button"
                onClick={handleAddElectricityEntry}
                data-testid="button-add-electricity"
                disabled={disabled}
              >
                <Plus className="mr-2 h-3 w-3" /> {"Add Electricity Entry"}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h5 className="text-sm font-bold leading-5 text-slate-700">{"Electricity Entries"}</h5>
            {electricityEntries.length > 0 && (
              <span className="rounded-md bg-primary/10 px-2 py-1 text-[10px] font-bold leading-4 text-primary">
                {`${electricityEntries.length} entries`}
              </span>
            )}
          </div>

          {electricityEntries.length > 0 && (
            <div className="flex flex-col gap-2">
              {electricityEntries.map((entry) => (
                <div
                  key={entry.id}
                  className={cn(
                    "flex flex-col gap-3 rounded-[24px] border border-slate-100 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between",
                    editingElectricityEntryId === entry.id && "outline outline-2 outline-primary/30"
                  )}
                  data-testid={`elec-entry-${entry.id}`}
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <EntryDot className="bg-sky-400" />
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium leading-5 text-slate-900">
                        <span>
                          {entry.consumption} {entry.unit.toUpperCase()}
                        </span>
                        <EntrySeparator />
                        <span className="text-slate-500">
                          {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                        </span>
                        {entry.meterId && (
                          <>
                            <EntrySeparator />
                            <span className="text-slate-500">
                              {`Meter/Site: ${entry.meterId}`}
                            </span>
                          </>
                        )}
                      </div>

                      {(entry.selfGeneratedPercent ?? 0) > 0 && (
                        <div className="flex flex-wrap items-center gap-3 text-xs font-medium leading-4 text-slate-500">
                          <span>{`Grid: ${entry.gridPercent ?? 100}%`}</span>
                          <span>{`Self-generated: ${entry.selfGeneratedPercent ?? 0}%`}</span>
                          {entry.isRenewable && (
                            <span>
                              {`Renewable: ${
                                entry.renewableSource === "solar"
                                  ? "Solar"
                                  : entry.renewableSource === "wind"
                                    ? "Wind"
                                    : entry.renewableSource === "hydro"
                                      ? "Hydro"
                                      : entry.renewableSource === "other"
                                        ? "Other"
                                        : "Solar"
                              }`}
                            </span>
                          )}
                        </div>
                      )}

                      {entry.evidenceId && entry.evidenceName && (
                        <button
                          type="button"
                          onClick={() => window.open(`/api/documents/${entry.evidenceId}`, "_blank")}
                          className="flex items-center gap-2 text-xs font-medium leading-4 text-slate-500 hover:text-slate-700"
                        >
                          <Paperclip className="h-3 w-3" />
                          {entry.evidenceName}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-6 self-end md:self-auto">
                    {entry.calculating ? (
                      <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {"Calculating..."}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        {entry.tco2e !== null
                          ? `${entry.tco2e.toFixed(3)} tCO2e`
                          : "N/A"}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditElectricityEntry(entry)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      disabled={disabled || (editingElectricityEntryId !== null && editingElectricityEntryId !== entry.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteElectricityEntry(entry.id)}
                      className="h-8 w-8 rounded-full p-0 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                      data-testid={`button-delete-elec-${entry.id}`}
                      disabled={disabled || editingElectricityEntryId !== null}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showElecCoverageWarning && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Electricity entries don't cover the full assessment period.
            </div>
          )}
        </div>
      </SectionCard>

      <SummaryMetricCard
        icon={<Zap className="h-6 w-6" />}
        title={"Scope 2 Total"}
        description={"Indirect emissions from purchased electricity"}
        value={scope2Total.toFixed(3)}
        unit={"tCO2e"}
        shellClassName="border border-primary/10 bg-primary/10 outline outline-2 outline-offset-[-2px] outline-primary/10"
        valueTestId="text-scope2-total"
      />

      <section className="overflow-hidden rounded-[28px] border border-primary/10 bg-primary/10 p-8 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-primary/10">
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white">
                <Users className="h-6 w-6" />
              </div>
              <div className="flex flex-col gap-1">
                <h4 className="text-xl font-bold leading-7 text-primary">{"Scope 3 Emissions - Detailed Data Entry"}</h4>
                <p className="text-sm leading-5 text-gray-500">{"Emissions linked to your business activities outside direct operations — e.g., employee travel, deliveries, working from home"}</p>
              </div>
            </div>
            <div className="text-left lg:text-right">
              <div className="text-4xl font-bold leading-10 text-primary" data-testid="text-scope3-total">
                {scope3Total.toFixed(3)}
              </div>
              <div className="text-xs font-semibold leading-4 text-gray-400">{"tCO2e"}</div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between  wizard-card">
              <div>
                <div className="text-base font-bold leading-6 text-primary">{"Homeworking"}</div>
                <div className="text-xs leading-4 text-slate-400">{"Calculated from employee count & homeworking % in General Information"}</div>
              </div>
              <div className="text-base font-bold leading-6 text-gray-900" data-testid="text-scope3-homeworking">
                {`${scope3Homeworking.toFixed(3)} tCO2e`}
              </div>
            </div>

            <Scope3Row
              title={"Business Travel"}
              description={"Flights, trains, buses, taxis, rental cars"}
              value={`${scope3BusinessTravel.toFixed(3)} tCO2e`}
              count={scope3Data.businessTravelEntries.length}
              open={expandedScope3Sections.has("businessTravel")}
              onToggle={() => toggleScope3Section("businessTravel")}
              disabled={disabled}
              testId="text-scope3-business-travel"
            >
              <BusinessTravelFormSection isOpen={expandedScope3Sections.has("businessTravel")} />
            </Scope3Row>

            <Scope3Row
              title="Hotel Accommodation"
              description="Scope 3 Cat. 6 — hotel nights during business travel"
              value={`${scope3Accommodation.toFixed(3)} tCO₂e`}
              count={scope3Data.accommodationEntries.length}
              open={expandedScope3Sections.has("accommodation")}
              onToggle={() => toggleScope3Section("accommodation")}
              disabled={disabled}
            >
              <AccommodationFormSection isOpen={expandedScope3Sections.has("accommodation")} />
            </Scope3Row>

            <Scope3Row
              title={"Employee Commuting"}
              description={"Daily travel between home and workplace"}
              value={`${scope3EmployeeCommuting.toFixed(3)} tCO2e`}
              count={scope3Data.employeeCommutingEntries.length}
              open={expandedScope3Sections.has("employeeCommuting")}
              onToggle={() => toggleScope3Section("employeeCommuting")}
              disabled={disabled}
              testId="text-scope3-commuting"
            >
              <EmployeeCommutingFormSection isOpen={expandedScope3Sections.has("employeeCommuting")} />
            </Scope3Row>

            <Scope3Row
              title={"Freight & Logistics"}
              description={"Upstream & downstream transport of goods"}
              value={`${scope3Freight.toFixed(3)} tCO2e`}
              count={scope3Data.freightEntries.length}
              open={expandedScope3Sections.has("freight")}
              onToggle={() => toggleScope3Section("freight")}
              disabled={disabled}
              testId="text-scope3-freight"
            >
              <FreightTransportFormSection isOpen={expandedScope3Sections.has("freight")} />
            </Scope3Row>

            <Scope3Row
              title={"Purchased Goods & Services"}
              description={"Spend-based emissions from company purchases"}
              value={`${scope3CompanyExpenses.toFixed(3)} tCO2e`}
              count={scope3Data.spendBasedEntries.length}
              open={expandedScope3Sections.has("companyExpenses")}
              onToggle={() => toggleScope3Section("companyExpenses")}
              disabled={disabled}
            >
              <CompanyExpensesFormSection isOpen={expandedScope3Sections.has("companyExpenses")} />
            </Scope3Row>

            <div className="wizard-card">
              <div>
                <div className="text-base font-bold leading-6 text-primary">{"Waste Disposal"}</div>
                <div className="text-xs leading-4 text-gray-400">{"Configure waste recycling and disposal in the Waste card"}</div>
              </div>
              <div className="text-base font-bold leading-6 text-gray-900" data-testid="text-scope3-waste">
                {`${scope3Waste.toFixed(3)} tCO2e`}
              </div>
            </div>

            <div className="wizard-card">
              <div>
                <div className="text-base font-bold leading-6 text-primary">{"Water Supply & Treatment"}</div>
                <div className="text-xs leading-4 text-gray-400">{"Configure water consumption in the Water card"}</div>
              </div>
              <div className="text-base font-bold leading-6 text-gray-900" data-testid="text-scope3-water">
                {`${scope3Water.toFixed(3)} tCO2e`}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="text-lg font-bold leading-7 text-gray-900">{"Total Scope 3 Emissions:"}</div>
              <div className="text-3xl font-bold leading-9 text-gray-900">
                {`${scope3Total.toFixed(3)} tCO2e`}
              </div>
            </div>
            {scope3Total === 0 && (
              <p className="mt-4 text-[10px] font-normal leading-4 text-gray-400">{"No Scope 3 detailed entries yet"}</p>
            )}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[28px] bg-primary p-10 text-white shadow-xl outline outline-1 outline-offset-[-1px] outline-primary/20">
        <div className="relative">
          <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-white/20 blur-[50px]" />
          <div className="absolute -right-8 -top-24 h-64 w-64 rounded-full bg-white/30 blur-3xl" />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-500">
                <Cloud className="h-6 w-6" />
              </div>
              <div className="flex flex-col gap-3">
                <h4 className="text-3xl font-black leading-9 text-white">{"Total Carbon Footprint"}</h4>
                <p className="text-base font-medium leading-6 text-white">Scope 1 + Scope 2 + Scope 3</p>
              </div>
            </div>
            <div className="flex flex-col gap-4 lg:items-end">
              <div className="flex items-end gap-2">
                <div className="text-6xl font-black leading-[60px] text-white" data-testid="text-total-emissions">
                  {totalEmissions.toFixed(3)}
                </div>
                <div className="pb-2 text-xl font-bold leading-7 text-white">{"tCO2e"}</div>
              </div>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs leading-4 text-white">
                  <EntryDot className="bg-white" />
                  <span data-testid="text-scope1-total-summary">Scope 1 : {scope1Total.toFixed(3)}</span>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs leading-4 text-white">
                  <EntryDot className="bg-white" />
                  <span data-testid="text-scope2-total-summary">Scope 2 : {scope2Total.toFixed(3)}</span>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs leading-4 text-white">
                  <EntryDot className="bg-white" />
                  <span data-testid="text-scope3-total-summary">Scope 3 : {scope3Total.toFixed(3)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[28px] bg-slate-800 p-10 text-white shadow-xl outline outline-1 outline-offset-[-1px] outline-slate-700">
        <div className="relative">
          <div className="absolute -left-20 top-0 h-64 w-64 rounded-full bg-white/10 blur-[50px]" />
          <div className="absolute -right-8 -top-24 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-700">
                <BarChart2 className="h-6 w-6" />
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <h4 className="text-3xl font-black leading-9 text-white">{"GHG Intensity"}</h4>
               
                </div>
                <p className="text-base font-medium leading-6 text-slate-300">{"Scope 1 + 2 + 3 emissions per unit of Net Turnover"}</p>
              </div>
            </div>

            <div className="flex flex-col gap-4 lg:items-end">
              {ghgIntensity !== null ? (
                <>
                  {(() => {
                    const { displayValue, unitCurrency } = scaleGHGIntensity(ghgIntensity, reportingCurrency ?? "");
                    return (
                      <div className="flex items-end gap-2">
                        <div className="text-6xl font-black leading-[60px] text-white" data-testid="text-ghg-intensity">
                          {displayValue}
                        </div>
                        <div className="pb-2 text-xl font-bold leading-7 text-slate-300">
                          {`tCO₂e / ${unitCurrency}`}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs leading-4 text-white">
                      <EntryDot className="bg-white" />
                      <span>{`${totalEmissions.toFixed(3)} tCO2e`}</span>
                    </div>
                    <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs leading-4 text-white">
                      <EntryDot className="bg-white" />
                      <span>{reportingCurrency} {parseFloat(netTurnover ?? "0").toLocaleString()}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex max-w-sm items-start gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm leading-5 text-amber-200">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                  <span>{"Net Turnover is required to calculate GHG Intensity. Enter it in General → Financial Information."}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <SectionCard header={
        <div className="flex items-center gap-3">
          <UploadSectionIcon />
          <h4 className="text-lg font-bold leading-7 text-slate-900">Supporting Documents</h4>
        </div>}>

        <DocumentUploadSection
          fieldIdentifier="environment-carbon-emissions"
          documents={carbonSupportingDocuments}
          onDocumentsChange={setCarbonSupportingDocuments}
          currentTab="environment"
          label={"Supporting Documents"}
          description={"Attach electricity bills, fuel receipts, and refrigerant service records for the reporting year"}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
          maxFiles={10}
          hideHeader
          className="rounded-[28px] border-2 border-dashed border-slate-200 bg-slate-50 p-6"
          itemClassName="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3"
          documentsListClassName="space-y-3"
          actionButtonClassName="h-8 rounded-2xl border-slate-200 bg-white px-3 text-xs font-bold text-slate-900 hover:bg-slate-50"
          emptyStateButtonClassName="w-full"
          emptyStateContent={
            <div className="flex flex-col items-center gap-4 rounded-[28px] px-4 py-10 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-500/5 text-slate-500">
                <Paperclip className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <div className="text-base font-bold leading-6 text-slate-800">Click to upload or drag and drop</div>
                <div className="max-w-3xl text-sm leading-5 text-slate-500">
                  {"Attach electricity bills, fuel receipts, and refrigerant service records for the reporting year"}
                </div>
              </div>
            </div>
          }
        />
      </SectionCard>

      <TransitionMeasuresBlock topicId="carbon-emissions" currentTab="environment" disabled={disabled} />

      <ProgressCard completion={carbonCompletion} />
    </div>
  );
}
